import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '@/services/firebase';
import { withTimeout } from './live-update-startup';

/**
 * Remote kill switch and sampling for Session Replay, read from Firestore
 * (`appConfig/sessionReplay` in the build's Firebase project, alongside `appVersions`).
 *
 *   { enabled: true, sampleRate: 25 }   // record 25% of app sessions of consenting users
 *
 * It can only restrict: consent is always required as well. Anything else (no document,
 * unreadable, a timeout, enabled not exactly true, an invalid rate) means off.
 */
export type SessionReplayRemoteConfig = { enabled: boolean; sampleRate: number };

export const SESSION_REPLAY_CONFIG_PATH = ['appConfig', 'sessionReplay'] as const;
export const SESSION_REPLAY_CONFIG_TIMEOUT_MS = 3000;
export const SESSION_REPLAY_OFF: SessionReplayRemoteConfig = Object.freeze({ enabled: false, sampleRate: 0 });

export const parseSessionReplayConfig = (data: unknown): SessionReplayRemoteConfig => {
  if (!data || typeof data !== 'object') return SESSION_REPLAY_OFF;
  const { enabled, sampleRate } = data as Record<string, unknown>;
  if (enabled !== true || typeof sampleRate !== 'number' || !Number.isFinite(sampleRate)) return SESSION_REPLAY_OFF;
  const rate = Math.min(100, Math.max(0, sampleRate));
  return rate > 0 ? { enabled: true, sampleRate: rate } : SESSION_REPLAY_OFF;
};

export const readSessionReplayConfig = async (): Promise<SessionReplayRemoteConfig> => {
  try {
    const snapshot = await withTimeout(
      getDoc(doc(firestore, ...SESSION_REPLAY_CONFIG_PATH)),
      SESSION_REPLAY_CONFIG_TIMEOUT_MS,
      'sessionReplayConfig'
    );
    return parseSessionReplayConfig(snapshot.data());
  } catch {
    return SESSION_REPLAY_OFF;
  }
};
